// client/src/hooks/useTeam.ts
//
// Carga el estado de team del usuario autenticado.
// Un usuario puede estar en múltiples teams; este hook los expone todos.
// Acciones disponibles: toggle sync, leave team (con opción de anonimizar).

import { useState, useEffect, useCallback, useRef } from 'react';
import { User } from '@supabase/supabase-js';
import { supabase, SUPABASE_ENABLED } from '../lib/supabase';

// ─── Tipos ────────────────────────────────────────────────────────────────────

export interface Team {
  id: string;
  name: string;
  slug: string;
  seatsTotal: number;
  ownerUserId: string;
  publicDisplay: boolean;
  publicDisplayName: string | null;
  createdAt: string;
}

export interface TeamMembership {
  id: string;
  teamId: string;
  userId: string;
  role: 'owner' | 'player';
  status: 'invited' | 'active' | 'left';
  syncEnabled: boolean;
  joinedAt: string | null;
  leftAt: string | null;
}

export interface TeamMemberWithStats {
  membership:  TeamMembership;
  email:       string | null;
  displayName: string | null;
  handsCount:  number | null;
  lastSync:    string | null;
  syncCount:   number | null;
}

export interface TeamContext {
  team: Team;
  membership: TeamMembership;
}

export interface PendingInvite {
  id:          string;
  email:       string;
  playerName:  string | null;
  expiresAt:   string;
  createdAt:   string;
}

export interface TeamState {
  loading: boolean;
  teams: TeamContext[];
  toggleSync:       (teamId: string, enabled: boolean) => Promise<boolean>;
  leaveTeam:        (teamId: string, anonymize: boolean) => Promise<boolean>;
  revokeInvite:     (inviteId: string) => Promise<boolean>;
  transferOwnership:(teamId: string, newOwnerId: string) => Promise<boolean>;
  loadDashboardData: (teamId: string) => Promise<{
    memberships:     TeamMemberWithStats[];
    activeSeats:     number;
    pendingSeats:    number;
    pendingInvites:  PendingInvite[];
  } | null>;
  refresh: () => void;
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useTeam(user: User | null): TeamState {
  const [loading, setLoading]       = useState(true);
  const [teams, setTeams]           = useState<TeamContext[]>([]);
  const [refreshKey, setRefreshKey] = useState(0);
  const initialLoadDone             = useRef(false);
  // Ref para acceder al user actual en callbacks sin que cambios de referencia
  // del objeto (token refresh) disparen re-renders innecesarios.
  const userRef = useRef(user);
  useEffect(() => { userRef.current = user; }, [user]);

  const refresh = useCallback(() => setRefreshKey(k => k + 1), []);

  useEffect(() => {
    if (!SUPABASE_ENABLED || !user) {
      setTeams([]);
      setLoading(false);
      return;
    }

    let cancelled = false;

    (async () => {
      // Solo mostrar spinner en la primera carga — los refetches por token refresh
      // de Supabase no deben causar un flash de loading si ya hay datos.
      if (!initialLoadDone.current) setLoading(true);
      try {
        const { data, error } = await supabase
          .from('team_memberships')
          .select(`
            id, team_id, user_id, role, status, sync_enabled, joined_at, left_at,
            teams (id, name, slug, seats_total, owner_user_id, public_display, public_display_name, created_at)
          `)
          .eq('user_id', user.id)
          .in('status', ['invited', 'active']);

        if (cancelled) return;

        if (error) {
          console.warn('[useTeam] fetch:', error.message);
          setTeams([]);
          return;
        }

        const contexts: TeamContext[] = (data || []).map((row: any) => ({
          team: {
            id:                row.teams.id,
            name:              row.teams.name,
            slug:              row.teams.slug,
            seatsTotal:        row.teams.seats_total,
            ownerUserId:       row.teams.owner_user_id,
            publicDisplay:     row.teams.public_display,
            publicDisplayName: row.teams.public_display_name,
            createdAt:         row.teams.created_at,
          },
          membership: {
            id:          row.id,
            teamId:      row.team_id,
            userId:      row.user_id,
            role:        row.role,
            status:      row.status,
            syncEnabled: row.sync_enabled,
            joinedAt:    row.joined_at,
            leftAt:      row.left_at,
          },
        }));

        setTeams(contexts);
        initialLoadDone.current = true;
      } catch (err) {
        if (!cancelled) console.error('[useTeam] unexpected:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, refreshKey]);

  // ── toggleSync ───────────────────────────────────────────────────────────────

  const toggleSync = useCallback(async (teamId: string, enabled: boolean): Promise<boolean> => {
    const user = userRef.current;
    if (!SUPABASE_ENABLED || !user) return false;

    const { error } = await supabase
      .from('team_memberships')
      .update({ sync_enabled: enabled })
      .eq('team_id', teamId)
      .eq('user_id', user.id);

    if (error) {
      console.error('[useTeam] toggleSync:', error.message);
      return false;
    }

    // Audit log (best-effort)
    const ctx = teams.find(c => c.team.id === teamId);
    await supabase.from('team_audit_log').insert({
      team_id:       teamId,
      actor_user_id: user.id,
      target_user_id: user.id,
      action:        enabled ? 'sync_enabled' : 'sync_disabled',
      metadata:      { membership_id: ctx?.membership.id },
    });

    // Actualizar estado local sin refetch
    setTeams(prev => prev.map(c =>
      c.team.id === teamId
        ? { ...c, membership: { ...c.membership, syncEnabled: enabled } }
        : c
    ));

    return true;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teams]);

  // ── leaveTeam ────────────────────────────────────────────────────────────────

  const leaveTeam = useCallback(async (teamId: string, anonymize: boolean): Promise<boolean> => {
    const user = userRef.current;
    if (!SUPABASE_ENABLED || !user) return false;

    // 1. Marcar membresía como 'left' y desactivar sync
    const { error: memberErr } = await supabase
      .from('team_memberships')
      .update({
        status:       'left',
        sync_enabled: false,
        left_at:      new Date().toISOString(),
      })
      .eq('team_id', teamId)
      .eq('user_id', user.id);

    if (memberErr) {
      console.error('[useTeam] leaveTeam membership update:', memberErr.message);
      return false;
    }

    // 2. Manejar los stats históricos según la elección del jugador
    if (anonymize) {
      // Opción A: anonimizar — nullear user_id en el row de team_stats.
      // El row queda como contribución histórica desasociada del individuo.
      const { error: anonErr } = await supabase
        .from('team_stats')
        .update({ user_id: null })
        .eq('team_id', teamId)
        .eq('user_id', user.id);

      if (anonErr) {
        // Non-fatal: la membresía ya está marcada como left.
        console.warn('[useTeam] anonimización de team_stats:', anonErr.message);
      }
    } else {
      // Opción B: borrar todo
      const { error: deleteErr } = await supabase
        .from('team_stats')
        .delete()
        .eq('team_id', teamId)
        .eq('user_id', user.id);

      if (deleteErr) {
        console.warn('[useTeam] delete team_stats:', deleteErr.message);
      }
    }

    // 3. Audit log (best-effort)
    await supabase.from('team_audit_log').insert({
      team_id:        teamId,
      actor_user_id:  user.id,
      target_user_id: user.id,
      action:         'member_left',
      metadata:       { anonymized: anonymize },
    });

    // Quitar el team del estado local
    setTeams(prev => prev.filter(c => c.team.id !== teamId));
    return true;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── loadDashboardData (owner only) ───────────────────────────────────────────

  const loadDashboardData = useCallback(async (teamId: string): Promise<{
    memberships:    TeamMemberWithStats[];
    activeSeats:    number;
    pendingSeats:   number;
    pendingInvites: PendingInvite[];
  } | null> => {
    const user = userRef.current;
    if (!SUPABASE_ENABLED || !user) return null;

    const now = new Date().toISOString();

    const [membResult, statsResult, invitesResult] = await Promise.all([
      supabase
        .from('team_memberships')
        .select('id, team_id, user_id, role, status, sync_enabled, joined_at, left_at, invited_email, display_name')
        .eq('team_id', teamId)
        .in('status', ['invited', 'active'])
        .order('joined_at', { ascending: true }),
      supabase
        .from('team_stats')
        .select('user_id, hands_count, updated_at, sync_count')
        .eq('team_id', teamId),
      // Invites pendientes: necesitamos tanto el conteo (para seats) como los datos (para mostrar y revocar)
      supabase
        .from('team_invitations')
        .select('id, email, player_name, expires_at, created_at')
        .eq('team_id', teamId)
        .is('accepted_at', null)
        .gt('expires_at', now)
        .order('created_at', { ascending: false }),
    ]);

    if (membResult.error) {
      console.error('[useTeam] loadDashboardData members:', membResult.error.message);
      return null;
    }

    // Email: usar invited_email del membership (disponible sin RLS cross-user).
    // Fallback: intentar profiles (funciona solo para el propio usuario del coach).

    const statsMap = new Map<string, { handsCount: number; lastSync: string; syncCount: number }>();
    for (const s of statsResult.data ?? []) {
      if (s.user_id) {
        statsMap.set(s.user_id, {
          handsCount: s.hands_count,
          lastSync:   s.updated_at,
          syncCount:  s.sync_count,
        });
      }
    }

    const memberships: TeamMemberWithStats[] = (membResult.data ?? []).map((row: any) => ({
      membership: {
        id:          row.id,
        teamId:      row.team_id,
        userId:      row.user_id,
        role:        row.role,
        status:      row.status,
        syncEnabled: row.sync_enabled,
        joinedAt:    row.joined_at,
        leftAt:      row.left_at,
      },
      email:       row.invited_email ?? null,
      displayName: row.display_name ?? null,
      handsCount:  statsMap.get(row.user_id)?.handsCount ?? null,
      lastSync:    statsMap.get(row.user_id)?.lastSync   ?? null,
      syncCount:   statsMap.get(row.user_id)?.syncCount  ?? null,
    }));

    const pendingInvites: PendingInvite[] = (invitesResult.data ?? []).map((r: any) => ({
      id:         r.id,
      email:      r.email,
      playerName: r.player_name ?? null,
      expiresAt:  r.expires_at,
      createdAt:  r.created_at,
    }));

    const activeSeats  = memberships.filter(m => m.membership.role === 'player' && m.membership.status === 'active').length;
    const pendingSeats = pendingInvites.length;

    return { memberships, activeSeats, pendingSeats, pendingInvites };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── revokeInvite ─────────────────────────────────────────────────────────────

  const revokeInvite = useCallback(async (inviteId: string): Promise<boolean> => {
    if (!SUPABASE_ENABLED) return false;
    // Marcar como expirada inmediatamente (RLS: invitations_owner_update lo permite)
    const { error } = await supabase
      .from('team_invitations')
      .update({ expires_at: new Date().toISOString() })
      .eq('id', inviteId);
    if (error) {
      console.error('[useTeam] revokeInvite:', error.message);
      return false;
    }
    return true;
  }, []);

  // ── transferOwnership ────────────────────────────────────────────────────────

  const transferOwnership = useCallback(async (teamId: string, newOwnerId: string): Promise<boolean> => {
    if (!SUPABASE_ENABLED) return false;
    const { error } = await supabase.rpc('transfer_team_ownership', {
      p_team_id:   teamId,
      p_new_owner: newOwnerId,
    });
    if (error) {
      console.error('[useTeam] transferOwnership:', error.message);
      return false;
    }
    return true;
  }, []);

  return { loading, teams, toggleSync, leaveTeam, revokeInvite, transferOwnership, loadDashboardData, refresh };
}
